import { VFC } from "react";

import { Button } from "@material-ui/core";
import { portexColor } from "@portex-pro/ui-components";
import { BrokerContractType } from "api/rest/rfp/brokerBidContractApi/types/BrokerContractType";
import ModeIcon from "components/ModeIcon";
import CursorPaginationTableControlsView from "components/pagination/CursorPaginationTableControlsView";
import PortexLink from "components/PortexLink";
import TableView, { TableViewColumns, TableViewRows } from "components/TableView";
import Text from "components/Text";
import { DateTime } from "luxon";

import BrokerRfpStateBadgeView from "./BrokerRfpStateBadgeView";

interface BrokerBidsIndexTableViewProps {
  items: BrokerContractType[];
  paginationOptions: {
    next: () => void;
    prev: () => void;
    setTake: (take: number) => void;
    take: number;
    page: number;
    total: number;
  };
  isLoading?: boolean;
}

const BrokerBidsIndexTableView: VFC<BrokerBidsIndexTableViewProps> = ({ items, paginationOptions, isLoading }) => {
  const columns: TableViewColumns<BrokerContractType> = [
    {
      name: "Bid Request",
      renderCell: (item) => (
        <div className="flex flex-col">
          <Text size="small" weight="bold">
            {item.portex_id}
          </Text>
          <div>
            <BrokerRfpStateBadgeView contract={item} />
          </div>
        </div>
      ),
      cellProps: { style: { maxWidth: "70%", width: "100%", alignContent: "center" } },
    },
    {
      name: "Shipper",
      renderCell: (item) => <Text size="small">{item.shipper.name}</Text>,
      cellProps: { style: { alignContent: "center", minWidth: "200px" } },
    },
    {
      name: "Deadline",
      renderCell: (item) => {
        let deadline = "";
        if (DateTime.fromISO(item.deadline) < DateTime.now().minus({ days: 7 })) {
          deadline = DateTime.fromISO(item.deadline).toLocaleString(DateTime.DATE_MED);
        } else {
          deadline = DateTime.fromISO(item.deadline).toRelative() ?? "";
        }
        return <Text size="small">{deadline}</Text>;
      },
      cellProps: { style: { alignContent: "center", minWidth: "200px" } },
    },
    {
      name: "Bids Submitted",
      renderCell: (item) => <Text size="small">{item.bids_submitted}</Text>,
      cellProps: { style: { alignContent: "center" } },
    },
    {
      name: "Lanes",
      renderCell: (item) => <Text size="small">{item.lanes_count}</Text>,
      cellProps: { style: { alignContent: "center" } },
    },
    {
      name: "Mode",
      renderCell: (item) => <ModeIcon mode={item.mode} />,
      cellProps: { style: { alignContent: "center" } },
    },
    {
      name: "Equipment",
      renderCell: (item) => <Text size="small">{item.equipment}</Text>,
      cellProps: { style: { alignContent: "center" } },
    },
  ];

  const handleGetLoadUrl = (brokerToken: string) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("brokerToken", brokerToken);
    return `/broker/bids/submit?${searchParams.toString()}`;
  };

  const rows: TableViewRows<BrokerContractType> = {
    headerCellProps: {
      style: {
        backgroundColor: "#fff",
        borderBottom: `1px solid ${portexColor.grey200}`,
        whiteSpace: "nowrap",
      },
    },
    endingIcon: (item) => (
      <Button
        component={PortexLink}
        variant="contained"
        color="primary"
        style={{ minWidth: "max-content" }}
        to={handleGetLoadUrl(item.associated_broker_token ?? "")}
      >
        View Bid Request
      </Button>
    ),
  };

  return (
    <>
      <TableView
        tableProps={{ style: { borderCollapse: "separate" } }}
        columns={columns}
        items={items}
        isLoading={isLoading}
        rows={rows}
      />
      <CursorPaginationTableControlsView {...paginationOptions} />
    </>
  );
};

export default BrokerBidsIndexTableView;
